import type { FocusOutsideEvent } from "@kobalte/core";
import { Popover as PopoverPrimitive } from "@kobalte/core/popover";
import type { JSX } from "solid-js";
import { Show, splitProps } from "solid-js";
import { Popover, PopoverContent } from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useIsMobile";

export interface ResponsivePopoverProps {
  /** Controlled open state. */
  open: boolean;
  /**
   * Called with `false` on every dismissal path — outside click, Esc, and the
   * drawer's dismiss — identically in both presentations, so consumers' persist
   * -on-dismiss logic stays presentation-agnostic.
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Desktop-only anchor for the popover. A function is used as Kobalte's
   * `anchorRef` accessor (attach to an existing element); a plain element is
   * rendered inside `Popover.Anchor`. Ignored on mobile.
   */
  anchor?: JSX.Element | (() => Element);
  /** Rendered as the drawer header on mobile; hidden on desktop. */
  title?: string;
  /**
   * Desktop-only: hide the popover's built-in top-right close (X). Use when the
   * content owns its own dismissal (e.g. a "Fatto" button) and the design has
   * no X. Ignored on mobile (the drawer keeps its own close control).
   */
  hideCloseButton?: boolean;
  /**
   * Desktop-only: extra classes merged onto the popover content — e.g. a wider
   * `w-*` or larger `p-*` to give the surface more breathing room. Ignored on
   * mobile (the bottom drawer owns its own sizing).
   */
  contentClass?: string;
  /**
   * Desktop-only: sets `PopoverContent`'s `topLayer` — read its doc first, it
   * is only for content that neither contains nor opens another layer. Needed
   * when the popover opens from inside a modal dialog or sheet, which would
   * otherwise hide it from assistive tech. Ignored on mobile: the drawer is a
   * dialog of its own, and a nested dialog is not hidden by its parent.
   */
  topLayer?: boolean;
  /** Shared content slot, rendered identically in both modes. */
  children?: JSX.Element;
}

/**
 * Generic, reusable kit component: an anchored Kobalte popover on desktop and a
 * bottom drawer (Sheet `side="bottom"`) on mobile, switched by `useIsMobile()`.
 * One controlled `open`/`onOpenChange` API and one content slot for both modes.
 */
export const ResponsivePopover = (props: ResponsivePopoverProps) => {
  const [local] = splitProps(props, [
    "open",
    "onOpenChange",
    "anchor",
    "title",
    "hideCloseButton",
    "contentClass",
    "topLayer",
    "children",
  ]);

  const isMobile = useIsMobile();

  const isAccessorAnchor = () => typeof local.anchor === "function";
  const anchorRef = () =>
    isAccessorAnchor()
      ? ((local.anchor as () => Element)() as HTMLElement | undefined)
      : undefined;

  // Both presentations are portaled, but Solid re-emits delegated events
  // (onClick) through the component-owner tree — so a click inside the surface
  // bubbles to whatever wraps the anchor (e.g. a fully-clickable card) and
  // triggers it. Contain clicks at the surface boundary so the anchor's
  // ancestors never see them. Kobalte combines this with its own handlers, and
  // it does not affect outside-click dismissal (that fires on clicks *outside*).
  const containClick = (event: MouseEvent) => event.stopPropagation();

  // A popover dismisses when focus moves outside its content. When it is opened
  // from a menu, the menu closing hands focus back to its trigger (our anchor)
  // or to its own content — which would dismiss the popover the instant it
  // opened (the "clicking an item does nothing" bug). Veto dismissal for those
  // focus targets, mirroring how a native Kobalte popover excludes its own
  // trigger. Real outside clicks (pointer) and Escape still dismiss normally.
  const onFocusOutside = (event: FocusOutsideEvent) => {
    const target = event.detail.originalEvent.target;
    if (!(target instanceof Element)) {
      return;
    }
    const anchorEl = anchorRef();
    if (anchorEl?.contains(target) || target.closest('[role="menu"]')) {
      event.preventDefault();
    }
  };

  return (
    <Show
      when={isMobile()}
      fallback={
        <Popover
          open={local.open}
          onOpenChange={local.onOpenChange}
          anchorRef={anchorRef}
        >
          <Show when={local.anchor && !isAccessorAnchor()}>
            <PopoverPrimitive.Anchor>
              {local.anchor as JSX.Element}
            </PopoverPrimitive.Anchor>
          </Show>
          <PopoverContent
            data-responsive-popover-mode="popover"
            class={local.contentClass}
            hideCloseButton={local.hideCloseButton}
            topLayer={local.topLayer}
            onClick={containClick}
            onFocusOutside={onFocusOutside}
          >
            {local.children}
          </PopoverContent>
        </Popover>
      }
    >
      <Sheet open={local.open} onOpenChange={local.onOpenChange}>
        <SheetContent
          side="bottom"
          data-responsive-popover-mode="drawer"
          onClick={containClick}
        >
          <Show when={local.title}>
            {(title) => (
              <SheetHeader>
                <SheetTitle>{title()}</SheetTitle>
              </SheetHeader>
            )}
          </Show>
          {local.children}
        </SheetContent>
      </Sheet>
    </Show>
  );
};
